import { Table, Checkbox, Icon } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { ReportFragment, ReportStatus, Country, RankingReportFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { noFilterOption, useReportStore } from '../ReportStore'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { generateRankingReport } from '../../../graphql/reports/mutationsReport'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'

interface RankingReportsTableProps {
  reports?: ReportFragment[]
  reportsSelectedRows: string[]
  setReportsSelectedRows: (value: string[]) => void
  isLoading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  showMoreButtonVisible: boolean
  rankingReportsFilters: RankingReportFilterInput
}

export const RankingReportsTable: FC<RankingReportsTableProps> = ({
  reports,
  reportsSelectedRows,
  setReportsSelectedRows,
  isLoading,
  isLoadingMore,
  error,
  showMoreButtonVisible,
  rankingReportsFilters,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const [selectedCountry, countries] = useReportStore((state) => [state.selectedCountry, state.countries])
  const [reportIdLoadingGeneration, setReportIdLoadingGeneration] = useState<string | null>(null)

  if ((isLoading && !isLoadingMore) || error || !reports) {
    return (
      <BackgroundBox r height="withTabsAndInfoBox">
        <NewLoadingAndErrors isLoading={isLoading && !isLoadingMore} hasNoData={!reports} error={error} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  const columns: ColumnProps<ReportFragment>[] = [
    {
      title: () => {
        const processedReports = reports.filter((r) => r.status === ReportStatus.Processed)
        return (
          <>
            {processedReports.length > 0 && (
              <PerFlexRow align="middle" justify="center" width="fullWidth">
                <div style={{ marginLeft: '6px' }}>
                  <Checkbox
                    checked={processedReports.every((r) => reportsSelectedRows.includes(r.id))}
                    onChange={(e) => {
                      const checked = e.target.checked
                      if (checked) {
                        setReportsSelectedRows(processedReports.map((r) => r.id))
                      } else {
                        setReportsSelectedRows([])
                      }
                    }}
                  />
                </div>
              </PerFlexRow>
            )}
          </>
        )
      },
      key: 'checkReport',
      width: '5%',
      render: (rowData: ReportFragment) => (
        <PerFlexRow align="middle" justify="center" direction="column">
          <Checkbox
            name="checkReport"
            checked={reportsSelectedRows.includes(rowData.id)}
            onChange={(e) => {
              const checked = e.target.checked
              if (checked) {
                setReportsSelectedRows([...reportsSelectedRows, rowData.id])
              } else {
                setReportsSelectedRows(reportsSelectedRows.filter((f) => f !== rowData.id))
              }
            }}
            disabled={rowData.status !== ReportStatus.Processed}
          />
        </PerFlexRow>
      ),
    },
    {
      key: 'fileName',
      title: t('reports.table.filename'),
      className: 'textAlign-left',
      width: '30%',
      render: (rowData: ReportFragment) => <>{rowData.filename}</>,
    },
    {
      key: 'company',
      title: t('reports.table.company'),
      className: 'textAlign-left',
      width: '13%',
      render: (rowData: ReportFragment) => <>{rowData.companyName}</>,
    },
    {
      key: 'country',
      title: t('reports.table.country'),
      className: 'textAlign-left',
      width: '9%',
      render: (rowData: ReportFragment) => <>{rowData.country}</>,
    },
    {
      key: 'year',
      title: t('reports.table.year'),
      className: 'textAlign-left',
      width: '11%',
      render: (rowData: ReportFragment) => <>{rowData.filename.split('-')[1]}</>,
    },
    {
      key: 'size',
      title: t('reports.table.size'),
      className: 'textAlign-left',
      width: '9%',
      render: (rowData: ReportFragment) => <>{rowData.size}</>,
    },
    {
      key: 'format',
      title: t('reports.table.format'),
      width: '8%',
      className: 'textAlign-left',
      render: (rowData: ReportFragment) => <>{rowData.format}</>,
    },
    {
      key: 'date',
      title: t('reports.table.date'),
      width: '10%',
      className: 'textAlign-left',
      render: (rowData: ReportFragment) => <>{formatDateShort(rowData.date.toString())}</>,
    },
    {
      key: 'actions',
      title: t('reports.table.actions'),
      className: 'textAlign-left',
      width: '7%',
      render: (rowData: ReportFragment) => (
        <>
          {rowData.status === ReportStatus.Error ? (
            <PerButtonCircle
              color="warning"
              onClick={async () => {
                setReportIdLoadingGeneration(rowData.id)
                await generateRankingReport(
                  rowData.id,
                  selectedCountry.value === noFilterOption.value ? (countries as Country[]) : [selectedCountry.value as Country],
                  rankingReportsFilters,
                  {},
                )
                setReportIdLoadingGeneration(null)
              }}
              variant="pastel"
              isProcessing={{ active: reportIdLoadingGeneration === rowData.id }}
              tooltip={
                reportIdLoadingGeneration !== null
                  ? t('reports.table.rankingGenerationInProgressTooltip')
                  : t('reports.table.rankingGenerationErrorTooltip')
              }
              customIconComponent={<Icon type="reload" style={{ fontSize: 16 }} />}
              disabled={reportIdLoadingGeneration !== null}
            />
          ) : (
            <PerButtonCircle
              icon="download_fill"
              color="primary"
              variant="pastel"
              asLink={rowData.status === ReportStatus.Processed ? rowData.downloadLink : undefined}
              tooltip={rowData.status === ReportStatus.Processed ? t('reports.table.downloadTooltip') : undefined}
              disabled={rowData.status !== ReportStatus.Processed}
            />
          )}
        </>
      ),
    },
  ]

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithTabsAndInfoBoxAndShowMoreButton: showMoreButtonVisible,
          viewWithTabsAndInfoBoxOneLineHeader: !showMoreButtonVisible,
        })}
        dataSource={reports.map((data) => ({ ...data, key: data.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="noReport_stroke"
              heading={
                !selectedCountry || selectedCountry.value === noFilterOption.value
                  ? t('reports.table.empty')
                  : t('reports.table.filteredEmpty')
              }
            />
          ),
        }}
      />
    </>
  )
}
